/**
 * @file TradingDash.jsx
 * @description Provides trading dashboard interface and analytics
 *
 * @section Responsibilities
 * - Display trading dashboard
 * - Handle trading analytics
 * - Manage dashboard state
 * - Process trading data
 * - Handle dashboard errors
 * - Provide analytics controls
 *
 * @section Dependencies
 * - DashboardService: Dashboard management
 * - AnalyticsService: Analytics processing
 * - DataService: Data processing
 * - StateService: State management
 *
 * @section Integration Points
 * - Dashboard API: Dashboard operations
 * - Analytics System: Analytics processing
 * - Data System: Data processing
 * - Control System: Dashboard controls
 *
 * @section State Management
 * - dashboardState: Dashboard status
 * - analyticsState: Analytics status
 * - dataState: Data status
 * - errorState: Error handling
 *
 * @section Events
 * - onAnalytics: Analytics processing
 * - onDataProcess: Data processing
 * - onStateChange: State updates
 * - onError: Error handling
 *
 * @section Props
 * @prop {Object} dashboardConfig - Dashboard configuration
 * @prop {Function} onComplete - Completion callback
 * @prop {Object} initialData - Initial dashboard data
 * @prop {Boolean} autoUpdate - Controls auto-updates
 */

import React, { useState, useCallback, useEffect } from "react";
import Widget from "../Widget";
import DgButton from "../../../../components/DgButton";
import Swal from "sweetalert2";
import Request from "../../../../utils/fetchUtils";
import TradingBody from "./TradingBody";
import { OrderClasses, OrderSides } from "./tradingVars";
import Loader from "../Loader";
import DgModal from "../../../../components/DgModal";
import { formatAmount } from "../../../../utils/formatAmount";

function TradingDash({ strategy, closingPosition, handleFetchData }) {
  const [tradingType, setTradingType] = useState("standard");
  const [orderClass, setOrderClass] = useState("equity");
  const [symbol, setSymbol] = useState("");
  const [symbolData, setSymbolData] = useState([]);
  const [prefilledOrder, setPrefilledOrder] = useState([]);
  const [loading, setLoading] = useState(false);

  const [activeSymbol, setActiveSymbol] = useState({});

  const [strikeList, setStrikeList] = useState({});

  const [expiration, setExpiration] = useState();
  const [orderBody, setOrderBody] = useState({});

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    commission: 0,
    cost: 0,
    fees: 0,
    symbol: "",
    quantity: "",
    side: "",
    type: "",
    duration: "",
    result: "",
    order_cost: "",
    margin_change: 0,
    class: "",
  });

  const handleModalState = (open) => {
    setIsModalOpen(open);
  };

  const getExpiration = (exp) => {
    setExpiration(exp);
  };

  const getOrderData = (order) => {
    setOrderBody(order);
  };

  const handleSymbolChange = (event) => {
    setSymbol(event.target.value);
  };

  const handleOrderClassChange = (event) => {
    setOrderClass(event.target.value);
  };

  useEffect(() => {
    if (symbol) {
      getSymbolData();
    }
  }, [orderClass]);

  useEffect(() => {
    if (closingPosition) {
      setSymbol(closingPosition.symbol);
      prefillTradingForm(closingPosition.symbol, closingPosition.quantity);
    }
  }, [closingPosition]);

  useEffect(() => {
    if (expiration) {
      getSymbolStrikes();
    } else {
      setStrikeList(null);
    }
  }, [expiration]);

  const getSymbolData = useCallback(async (customSymbol) => {
    let symbolQuery;

    if (tradingType == "standard") {
      if (customSymbol) {
        symbolQuery = customSymbol;
      } else {
        symbolQuery = symbol;
      }
    } else {
      // Advanced symbols
    }

    setLoading(true);
    const res = await Request(
      `/trading/symbol?symbols=${symbolQuery}&orderClass=${orderClass}&strategyId=${strategy.strategyId}`
    );

    if (res.ok) {
      setLoading(false);
      setActiveSymbol(res.data[0]);
    } else {
      setLoading(false);
      Swal.fire({
        title: "Error consulting symbol",
        text: "Symbol does not exist",
        icon: "error",
      });
    }
  });

  const getSymbolStrikes = useCallback(async () => {
    let symbolQuery;

    if (tradingType == "standard") {
      symbolQuery = symbol;
    } else {
      // Advanced symbols
    }

    const res = await Request(
      `/trading/symbol/data?symbol=${symbolQuery}&expiration=${expiration}&type=strikes&strategyId=${strategy.strategyId}`
    );

    if (res.ok) {
      const strikesData = res.data?.options?.option;

      const filteredStrikes = strikesData.map(function (strike) {
        return strike.strike;
      });

      setStrikeList(filteredStrikes);
    }
  });

  const reset = () => {
    setSymbol("");
    setActiveSymbol("");
    setOrderBody({});
  };

  const orderValidation = (real) => {
    if (Object.keys(orderBody).length != 0) {
      if (
        Object.values(orderBody).some(
          (value) => value === "" || value === null || value === undefined
        )
      ) {
        Swal.fire({
          title: "Error previewing order",
          text: "Please make sure to fill out all the data",
          icon: "error",
        });
      } else {
        createOrder(real);
      }
    } else {
      Swal.fire({
        title: "Error previewing order",
        text: "Please fill out all the data",
        icon: "error",
      });
    }
  };

  const createOrder = async (real) => {
    const res = await Request(
      `/trading/strategy/${strategy.strategyId}/orders?preview=true`,
      orderBody,
      "POST"
    );

    //TODO: Handle error messages

    if (res.ok) {
      const orderPreviewCost = res.data?.order_cost;

      if (real) {
        const orderRes = await Request(
          `/trading/strategy/${strategy.strategyId}/orders`,
          {
            ...orderBody,
            order_cost: orderPreviewCost,
          },
          "POST"
        );

        if (orderRes.ok && orderRes.data) {
          setIsModalOpen(false);
          Swal.fire({
            title: "Order created",
            text: "Order created successfully",
            icon: "success",
          });
          reset();
          handleFetchData();
        }
      } else {
        setIsModalOpen(!isModalOpen);
        setModalData(res.data);
      }
    } else {
      Swal.fire({
        title: "Error creating order",
        text: "There was an error creating the order",
        icon: "error",
      });
    }
  };

  const prefillTradingForm = (closingSymbol, closingQty) => {
    getSymbolData(closingSymbol).then(() => {
      let tmpOrder = {
        quantity: 0,
        side: "",
      };
      if (closingQty > 0) {
        tmpOrder.side = OrderSides.SELL.value;
        tmpOrder.quantity = closingQty;
      } else {
        tmpOrder.side = OrderSides.BUY_TO_COVER.value;
        tmpOrder.quantity = closingQty * -1;
      }
      setPrefilledOrder(tmpOrder);
    });
  };

  return (
    <>
      <Widget name={"trading-dash"} width={"full-width"}>
        {loading && <Loader loading={loading}></Loader>}
        <h2 className="left-title">Trading</h2>
        <div className="trading-dash">
          <div className="trading-info">
            <div className="tab-group">
              <div className="tab active">
                <p>Standard Trading</p>
              </div>
              <div className="tab">{/* <p>Advanced Trading</p> */}</div>
            </div>
            <div className="row">
              <div className="search">
                <input
                  style={{ textTransform: "uppercase" }}
                  type="text"
                  placeholder="Enter an underlying symbol"
                  value={symbol}
                  onChange={handleSymbolChange}
                />
                <img
                  src="/assets/icons/search.svg"
                  alt=""
                  onClick={() => {
                    getSymbolData();
                  }}
                />
              </div>
              <div className="form-field first">
                <select
                  name=""
                  onChange={handleOrderClassChange}
                  value={orderClass}
                >
                  {Object.keys(OrderClasses).map((oClass) => (
                    <option
                      key={OrderClasses[oClass].value}
                      value={OrderClasses[oClass].value}
                    >
                      {OrderClasses[oClass].name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* TODO check if advanced and create advancedTradingBody*/}
            <TradingBody
              orderClass={orderClass}
              data={activeSymbol}
              setOrderBody={setOrderBody}
              getExpiration={getExpiration}
              prefilledOrder={prefilledOrder}
              updatedStrikes={strikeList}
            />
            <p className="info">
              Quotes for underlying Index values (e.g. SPX, VIX) are delayed 15
              minutes.
              <br />
              Commission/fees do not include regulatory or pass-through fees.
              <br />
              Stop Order Disclosure
            </p>
            <div className="buttons">
              <div>
                <DgButton
                  category="primary"
                  size="medium"
                  text="Submit"
                  action={() => {
                    orderValidation(false);
                  }}
                ></DgButton>
                <DgButton
                  category="secondary"
                  size="medium"
                  text="Preview"
                  action={() => {
                    orderValidation(false);
                  }}
                ></DgButton>
              </div>
              <DgButton
                category="tertiary"
                size="medium"
                text="Reset"
                action={() => {
                  reset();
                }}
              ></DgButton>
            </div>
          </div>
          <div className="market-info">
            <div className="market">
              {/* <p>Market is open.</p> */}
              <DgButton
                category="secondary"
                size="small"
                text="Preview"
                action={() => {
                  orderValidation(false);
                }}
              ></DgButton>
            </div>
            <div className="symbol">
              {Object.keys(activeSymbol).length === 0 ? (
                <span>Fill in the trade ticket to see pricing</span>
              ) : (
                <div>
                  <div className="line">
                    <span className="name">{activeSymbol?.symbol}</span>
                    <div>
                      <span className="cost">${activeSymbol?.last}</span>
                      <span
                        className={
                          "gainloss " +
                          (activeSymbol?.change < 0 ? "negative" : "")
                        }
                      >
                        (${activeSymbol?.change})
                      </span>
                    </div>
                  </div>
                  <div className="details">
                    <div className="detail">
                      <span className="prop">Bid</span>
                      <span className="val">
                        ${activeSymbol?.bid} x {activeSymbol?.bidsize}
                      </span>
                    </div>
                    <div className="detail">
                      <span className="prop">Ask</span>
                      <span className="val">
                        ${activeSymbol?.ask} x {activeSymbol?.asksize}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <DgModal
          modalState={isModalOpen}
          handleModalState={() => {
            setIsModalOpen(!isModalOpen);
          }}
        >
          <div className="modal-content preview">
            <h1 className="title">Preview order</h1>
            <div className="flex">
              <div className="data-group">
                <p className="data-field">
                  Commission: $ {formatAmount(modalData?.commission)}
                </p>
                <p className="data-field">
                  Cost: $ {formatAmount(modalData?.cost)}
                </p>
                <p className="data-field">
                  Fees: $ {formatAmount(modalData?.fees)}
                </p>
                <p className="data-field">
                  Order cost: $ {formatAmount(modalData?.order_cost)}
                </p>
                <p className="data-field">
                  Margin change: $ {formatAmount(modalData?.margin_change)}
                </p>
                <p className="data-field">Class: {modalData?.class}</p>
              </div>
              <div className="data-group">
                <p className="data-field">Symbol: {modalData?.symbol}</p>
                <p className="data-field">Quantity: {modalData?.quantity}</p>
                <p className="data-field">Side: {modalData?.side}</p>
                <p className="data-field">Type: {modalData?.type}</p>
                <p className="data-field">Duration: {modalData?.duration}</p>
              </div>
            </div>
            <div className="flex">
              <DgButton
                category="primary"
                size="large"
                text="Submit order"
                action={() => {
                  orderValidation(true);
                }}
              ></DgButton>
              <DgButton
                category="secondary"
                size="large"
                text="Cancel"
                action={() => {
                  handleModalState(!isModalOpen);
                }}
              ></DgButton>
            </div>
          </div>
        </DgModal>
      </Widget>
    </>
  );
}

export default TradingDash;
